import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCaptureGoldenRunMutation, useGetRunQuery, useReviewRunMutation } from './api/evalApi';

export default function EvalRunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const [reviewNotes, setReviewNotes] = useState('');
  const [goldenLabel, setGoldenLabel] = useState('');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const { data, isLoading, error } = useGetRunQuery(runId ?? '', { skip: !runId });
  const [reviewRun, { isLoading: isReviewing }] = useReviewRunMutation();
  const [captureGoldenRun, { isLoading: isCapturingGolden }] = useCaptureGoldenRunMutation();

  if (!runId) {
    return <Alert severity="error">Missing run id.</Alert>;
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return <Alert severity="error">Failed to load run.</Alert>;
  }

  const submitReview = async (
    verdict: 'confirmed_pass' | 'confirmed_fail' | 'override_pass' | 'override_fail'
  ) => {
    try {
      await reviewRun({
        run_id: data.id,
        review_verdict: verdict,
        review_notes: reviewNotes.trim() || undefined,
        requires_review: false,
      }).unwrap();
    } catch {
      // Mutation error is surfaced by RTK query state in caller contexts.
    }
  };

  const submitGoldenCapture = async () => {
    try {
      await captureGoldenRun({
        run_id: data.id,
        golden_label: goldenLabel.trim() || undefined,
      }).unwrap();
      setStatusMessage('Run added to golden dataset.');
    } catch {
      setStatusMessage('Failed to add run to golden dataset.');
    }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3 }}>
      {statusMessage && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setStatusMessage(null)}>
          {statusMessage}
        </Alert>
      )}

      <Typography variant="h4" sx={{ mb: 2 }}>
        Eval Run Detail
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Chip
            label={data.status}
            color={data.status === 'failed' ? 'error' : 'default'}
            size="small"
          />
          <Chip label={`${data.provider}:${data.model}`} size="small" />
          {data.is_golden && (
            <Tooltip title="Saved as a reference example" arrow>
              <Chip label="Golden" color="success" size="small" />
            </Tooltip>
          )}
          {data.is_regression && (
            <Tooltip title="This scored worse than a previous passing run" arrow>
              <Chip label="Regression" color="warning" size="small" />
            </Tooltip>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Run ID: {data.id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Case ID: {data.case_id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Triggered By: {data.triggered_by}
        </Typography>
        {data.review_verdict && (
          <Typography variant="body2" color="text.secondary">
            Review: {data.review_verdict}
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Golden Dataset
        </Typography>
        <TextField
          label="Golden Label"
          value={goldenLabel}
          onChange={(event) => setGoldenLabel(event.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 1.5 }}
          helperText="A tag to group this with similar reference examples"
        />
        <Tooltip title="Save this as a known-good reference for future comparison" arrow>
          <span>
            <Button
              size="small"
              variant="contained"
              onClick={submitGoldenCapture}
              disabled={isCapturingGolden || data.is_golden || data.status !== 'completed'}
            >
              {data.is_golden
                ? 'Already Golden'
                : isCapturingGolden
                  ? 'Saving...'
                  : 'Add to Golden Dataset'}
            </Button>
          </span>
        </Tooltip>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Review Actions
        </Typography>
        <TextField
          label="Review Notes"
          value={reviewNotes}
          onChange={(event) => setReviewNotes(event.target.value)}
          multiline
          rows={2}
          fullWidth
          sx={{ mb: 1.5 }}
        />
        <Stack direction="row" spacing={1}>
          <Tooltip title="Agree with the AI — this output is good" arrow>
            <Button
              size="small"
              variant="outlined"
              onClick={() => submitReview('confirmed_pass')}
              disabled={isReviewing}
            >
              Confirm Pass
            </Button>
          </Tooltip>
          <Tooltip title="Agree with the AI — this output needs improvement" arrow>
            <Button
              size="small"
              variant="outlined"
              onClick={() => submitReview('confirmed_fail')}
              disabled={isReviewing}
            >
              Confirm Fail
            </Button>
          </Tooltip>
          <Tooltip title="Disagree with the AI — mark this as good anyway" arrow>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => submitReview('override_pass')}
              disabled={isReviewing}
            >
              Override Pass
            </Button>
          </Tooltip>
          <Tooltip title="Disagree with the AI — mark this as bad despite the AI's score" arrow>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => submitReview('override_fail')}
              disabled={isReviewing}
            >
              Override Fail
            </Button>
          </Tooltip>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Output
        </Typography>
        <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', mb: 0 }}>
          {data.output || '(empty)'}
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Assertions
        </Typography>
        {(data.assertion_results ?? []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No assertion details available.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {(data.assertion_results ?? []).map((assertion, index) => (
              <Box key={`${assertion.type}-${index}`}>
                <Typography variant="body2">
                  {assertion.type}: {assertion.pass ? 'PASS' : 'FAIL'}
                </Typography>
                {assertion.message && (
                  <Typography variant="caption" color="text.secondary">
                    {assertion.message}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
